import { VarExpressionModel } from "../views/FlowDesigner/Models/VarExpressionModel";
import { ExpressionTypes } from "../views/FlowDesigner/Models/ExpressionTypes";
import { StringTemplateExpressionModel } from "../views/FlowDesigner/Models/StringTemplateModel";
import { CollectionExpressionModel } from "../views/FlowDesigner/Models/CollectionExpressionModel";
import { getCollectionExpressionLabel } from "../views/FlowDesigner/Models/getCollectionExpressionLabel";

/**
 * Safely evaluates a simple property path like "Operation.Name" or "Operation.Description"
 * @param path The property path string (e.g., "Operation.Name")
 * @param context The context object to traverse
 * @returns The value at the specified path or undefined if not found
 */
function getDisplayValueFromPath(path: string, context: Record<string, unknown>): unknown {
	if (!path || !context) {
		return undefined;
	}

	// Split the path by dots and traverse the object
	const pathParts = path.split('.');
	let current: unknown = context;

	for (const part of pathParts) {
		if (current === null || current === undefined) {
			return undefined;
		}

		// Only allow property access on objects
		if (typeof current === 'object') {
			current = (current as Record<string, unknown>)[part];
		} else {
			return undefined;
		}
	}

	// If the end value is a VarExpression (object with Type == 'Var'), return the concatenated Value array
	const varExpression = current as VarExpressionModel;
	if (varExpression && varExpression.Type === ExpressionTypes.Var && Array.isArray(varExpression.Value)) {
		return varExpression.Value.join('.');
	}

	const constExpression = current as VarExpressionModel;
	if (constExpression && constExpression.Type === ExpressionTypes.Const) {
		return constExpression.Value;
	}

	const stringTemplateExpression = current as StringTemplateExpressionModel;
	if (stringTemplateExpression && stringTemplateExpression.Type === ExpressionTypes.StringTemplate) {
		return stringTemplateExpression.Content.substring(0, 50); // Return a truncated version of the content for display purposes
	}

	const collectionExpression = current as CollectionExpressionModel;
	if (collectionExpression && collectionExpression.Type === ExpressionTypes.Collection) {
		return getCollectionExpressionLabel(collectionExpression);
	}

	return current;
}

// Cached regex patterns for performance
const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
const SCRIPT_TAG_REGEX = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
const EVENT_HANDLER_QUOTED_REGEX = /\s+on\w+\s*=\s*["'][^"']*["']/gi;
const EVENT_HANDLER_UNQUOTED_REGEX = /\s+on\w+\s*=\s*[^"'\s>]+/gi;
const JAVASCRIPT_PROTOCOL_QUOTED_REGEX = /(href|src)\s*=\s*["']javascript:[^"']*["']/gi;
const JAVASCRIPT_PROTOCOL_UNQUOTED_REGEX = /(href|src)\s*=\s*javascript:[^"'\s>]*/gi;
const PLACEHOLDER_REGEX = /\{\{([^}]+)\}\}/g;

/**
 * Checks if a string contains HTML markup
 * @param str The string to check
 * @returns True if the string contains HTML tags, false otherwise
 */
export function containsHtml(str: string): boolean {
	if (!str) return false;
	return HTML_TAG_REGEX.test(str);
}

/**
 * Basic HTML sanitization to prevent XSS attacks
 * Only allows safe HTML tags and removes dangerous attributes
 * @param html The HTML string to sanitize
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(html: string): string {
	if (!html) return html;

	let sanitized = html;

	// Remove script tags and their content
	sanitized = sanitized.replace(SCRIPT_TAG_REGEX, '');

	// Remove all event handler attributes (onclick, onmouseover, etc.)
	sanitized = sanitized.replace(EVENT_HANDLER_QUOTED_REGEX, '');
	sanitized = sanitized.replace(EVENT_HANDLER_UNQUOTED_REGEX, '');

	// Remove javascript: protocols in href and src attributes
	sanitized = sanitized.replace(JAVASCRIPT_PROTOCOL_QUOTED_REGEX, '$1=""');
	sanitized = sanitized.replace(JAVASCRIPT_PROTOCOL_UNQUOTED_REGEX, '$1=""');

	// For production use, consider using a library like DOMPurify for comprehensive sanitization
	return sanitized;
}

/**
 * Interpolates a template string with double brace placeholders {{PropertyName}}.
 * Supports dot notation for nested properties (e.g., {{Operation.Name}}).
 * The 'parameters.' prefix is optional and will be stripped if present.
 *
 * @param template The template string containing expressions like {{PropertyName}} or {{parameters.PropertyName}}
 * @param context The context object for property lookup
 * @param failOnUnresolved If true, returns undefined when any placeholder cannot be resolved; otherwise returns empty string for unresolved placeholders
 * @returns The interpolated string with expressions evaluated, or undefined if failOnUnresolved is true and resolution fails
 */
export function interpolateTemplate(template: string, context: Record<string, unknown>, failOnUnresolved: boolean = false): string | undefined {
	if (!template) {
		return template;
	}

	try {
		let hasUnresolvedPlaceholder = false;

		const result = template.replace(PLACEHOLDER_REGEX, (match, expression) => {
			try {
				const cleanExpression = expression.trim();

				// Support alternative expressions like "TextCollection|Text" — resolve first non-null value
				const alternatives = cleanExpression.split('|').map((alt: string) => alt.trim());

				let value: unknown;
				for (const alt of alternatives) {
					// Strip optional 'parameters.' prefix
					const propertyPath = alt.startsWith('parameters.') ? alt.substring('parameters.'.length) : alt;
					value = getDisplayValueFromPath(propertyPath, context);
					if (value !== null && value !== undefined) {
						break;
					}
				}

				if (value === null || value === undefined) {
					if (failOnUnresolved) {
						console.warn(`Placeholder ${match} in template "${template}" could not be resolved`);
						hasUnresolvedPlaceholder = true;
					}
					// Return empty string when value couldn't be resolved
					return '';
				}

				return String(value);
			} catch (error) {
				console.warn(`Failed to resolve placeholder:`, error);
				if (failOnUnresolved) {
					hasUnresolvedPlaceholder = true;
				}
				return '';
			}
		});

		// If failOnUnresolved is true and we couldn't resolve something, return undefined
		if (failOnUnresolved && hasUnresolvedPlaceholder) {
			return undefined;
		}

		return result;
	} catch (error) {
		console.warn('Failed to interpolate template:', template, error);
		return failOnUnresolved ? undefined : template;
	}
}
